<template>
  <div class="board-type-management">
    <div class="page-header">
      <h2>硬件版型管理</h2>
      <el-button type="primary" @click="handleAdd">新增版型</el-button>
    </div>

    <el-card class="box-card">
      <el-table
        v-loading="loading"
        :data="boardTypes"
        style="width: 100%"
        border
      >
        <el-table-column prop="id" label="ID" width="80" align="center" />
        <el-table-column prop="name" label="版型名称" width="180" />
        <el-table-column prop="description" label="描述" />
        <el-table-column prop="is_enabled" label="状态" width="100" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.is_enabled ? 'success' : 'info'">
              {{ scope.row.is_enabled ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center">
          <template #default="scope">
            <el-button size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-popconfirm
              title="确定要删除这个版型吗？建议优先使用禁用。"
              @confirm="handleDelete(scope.row)"
            >
              <template #reference>
                <el-button size="small" type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 编辑/新增对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="80px">
        <el-form-item label="名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入版型名称"></el-input>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input type="textarea" v-model="form.description" placeholder="请输入描述"></el-input>
        </el-form-item>
        <el-form-item label="状态" prop="is_enabled" v-if="isEdit">
          <el-switch
            v-model="form.is_enabled"
            active-text="启用"
            inactive-text="禁用"
          ></el-switch>
          <div class="tips">禁用后，新设备注册将无法选择此版型，现有设备不受影响。</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { getBoardTypes, createBoardType, updateBoardType, deleteBoardType } from '../api/device'

const loading = ref(false)
const boardTypes = ref([])
const dialogVisible = ref(false)
const submitting = ref(false)
const isEdit = ref(false)
const formRef = ref(null)

const form = ref({
  id: '',
  name: '',
  description: '',
  is_enabled: true
})

const rules = {
  name: [
    { required: true, message: '请输入版型名称', trigger: 'blur' },
    { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
  ]
}

const dialogTitle = computed(() => isEdit.value ? '编辑版型' : '新增版型')

const loadData = async () => {
  loading.value = true
  try {
    // 传入 all: true 以获取所有状态的版型
    const res = await getBoardTypes({ all: true })
    if (res.success) {
      boardTypes.value = res.data
    }
  } catch (error) {
    ElMessage.error('加载数据失败')
  } finally {
    loading.value = false
  }
}

const handleAdd = () => {
  isEdit.value = false
  form.value = {
    name: '',
    description: '',
    is_enabled: true
  }
  dialogVisible.value = true
}

const handleEdit = (row) => {
  isEdit.value = true
  form.value = {
    id: row.id,
    name: row.name,
    description: row.description,
    is_enabled: row.is_enabled
  }
  dialogVisible.value = true
}

const handleDelete = async (row) => {
  try {
    const res = await deleteBoardType(row.id)
    if (res.success) {
      ElMessage.success('删除成功')
      loadData()
    }
  } catch (error) {
    // 错误信息已经在 axios 拦截器或 api 方法中处理，或者这里显示后端返回的 message
    const msg = error.response?.data?.message || error.message || '删除失败'
    ElMessage.error(msg)
  }
}

const handleSubmit = async () => {
  if (!formRef.value) return
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      submitting.value = true
      try {
        if (isEdit.value) {
          await updateBoardType(form.value.id, form.value)
          ElMessage.success('更新成功')
        } else {
          await createBoardType(form.value)
          ElMessage.success('创建成功')
        }
        dialogVisible.value = false
        loadData()
      } catch (error) {
        const msg = error.response?.data?.message || error.message || '操作失败'
        ElMessage.error(msg)
      } finally {
        submitting.value = false
      }
    }
  })
}

onMounted(() => {
  loadData()
})
</script>

<style scoped>
.board-type-management {
  padding: 20px;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.tips {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}
</style>